/* Barre d'achat fixe (Vanille Désiré), mobile et ordinateur.
   Affiche la barre quand le bouton d'achat principal sort du viewport
   par le haut (défilement vers le bas de la fiche) et la retire dès qu'il
   redevient visible : elle ne le recouvre jamais.

   Sources de vérité pour la variante affichée (prix, format, disponibilité),
   dans l'ordre :
   1. l'événement document 'vd:variant-change' (detail { id, price, options,
      available }) émis par le nouveau sélecteur de la fiche ;
   2. en repli, 'change' sur le <select data-vd-variant-select> caché du
      formulaire (data-options séparé par « || », data-price, disabled) ;
   3. le miroir du bloc prix et du bouton principal (MutationObserver) :
      le bouton de la barre vit HORS du formulaire (attribut form=), il n'est
      donc pas mis à jour par le thème lui-même.

   La barre s'efface quand un tiroir est ouvert (body.vd-no-scroll). */

if (!customElements.get('vd-sticky-atc')) {
  customElements.define(
    'vd-sticky-atc',
    class VdStickyAtc extends HTMLElement {
      connectedCallback() {
        this.target =
          (this.dataset.targetSelector && document.querySelector(this.dataset.targetSelector)) ||
          document.getElementById(this.dataset.targetId) ||
          document.querySelector(`#MainProduct-${this.dataset.sectionId} .product-form`);

        this.button = this.querySelector('.vd-sticky-atc__button');
        this.priceTarget = this.querySelector('[data-vd-satc-price]') || this.querySelector('.vd-sticky-atc__price');
        this.formatTarget = this.querySelector('[data-vd-satc-format]');
        this.passed = false;

        this.render = this.render.bind(this);
        this.onVariantEvent = this.onVariantEvent.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);

        /* 1. Visibilité : le bouton principal est passé au-dessus du viewport. */
        if (this.target && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              });
              this.render();
            },
            { threshold: 0 }
          );
          this.observer.observe(this.target);
        }

        /* 1 bis. Repli : un saut de défilement (ancre, touche Fin, page
           rouverte déjà défilée) peut faire passer le bouton de « sous
           l'écran » à « au-dessus » sans jamais le traverser, et l'observer ne
           se déclenche alors pas. On recale l'état au défilement, une fois par
           image, sans rien faire tant que rien ne change. */
        if (this.target) {
          this.onScroll = this.onScroll.bind(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.onScroll();
        }

        /* 2. Tiroirs : body.vd-no-scroll est posé par vd-yc.js à l'ouverture. */
        if ('MutationObserver' in window) {
          this.bodyObserver = new MutationObserver(this.render);
          this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        }

        /* 3. Variante : événement du nouveau sélecteur, puis repli sur le select. */
        document.addEventListener('vd:variant-change', this.onVariantEvent);
        document.addEventListener('change', this.onSelectChange);

        /* 4. Miroirs existants : état et libellé du bouton principal, bloc prix. */
        if ('MutationObserver' in window) {
          if (this.button && this.target instanceof HTMLButtonElement) {
            this.syncButton = this.syncButton.bind(this);
            this.stateObserver = new MutationObserver(this.syncButton);
            this.stateObserver.observe(this.target, {
              attributes: true,
              attributeFilter: ['disabled'],
              childList: true,
              subtree: true,
              characterData: true,
            });
            this.syncButton();
          }

          // Le prix du bloc principal est réécrit à chaque changement de variante
          // (updatePrice dans assets/vd-yc.js) : sans ce miroir, la barre
          // afficherait le prix de la variante rendue côté serveur.
          this.priceSource = document.querySelector('[data-vd-pdp] .vd-pdp__price');
          if (this.priceSource && this.priceTarget) {
            this.syncPrice = this.syncPrice.bind(this);
            this.priceObserver = new MutationObserver(this.syncPrice);
            this.priceObserver.observe(this.priceSource, {
              childList: true,
              subtree: true,
              characterData: true,
            });
            this.syncPrice();
          }
        }

        /* Etat initial : si la page s'ouvre déjà défilée, on aligne la barre
           sur la variante réellement sélectionnée dans le formulaire. */
        const select = this.findSelect();
        if (select) this.applyOption(select.options[select.selectedIndex]);
        this.render();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.bodyObserver) this.bodyObserver.disconnect();
        if (this.stateObserver) this.stateObserver.disconnect();
        if (this.priceObserver) this.priceObserver.disconnect();
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
        if (this.frame) cancelAnimationFrame(this.frame);
        document.removeEventListener('vd:variant-change', this.onVariantEvent);
        document.removeEventListener('change', this.onSelectChange);
      }

      /* ---------- Visibilité ---------- */

      onScroll() {
        if (!this.target || this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          const r = this.target.getBoundingClientRect();
          // Un bouton sans boîte (display:none) ne compte pas comme « passé ».
          const passed = r.width || r.height ? r.bottom <= 0 : false;
          if (passed !== this.passed) {
            this.passed = passed;
            this.render();
          }
        });
      }

      render() {
        const drawerOpen = document.body.classList.contains('vd-no-scroll');
        const show = this.passed && !drawerOpen;
        this.classList.toggle('vd-sticky-atc--visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        // Hors écran, la barre ne doit ni recevoir le focus clavier ni un clic.
        if ('inert' in this) this.inert = !show;
      }

      /* ---------- Variante ---------- */

      findSelect() {
        return (
          document.querySelector('[data-vd-pdp-form] [data-vd-variant-select]') ||
          document.querySelector('[data-vd-variant-select]')
        );
      }

      optionFor(id) {
        const select = this.findSelect();
        const key = String(id == null ? '' : id);
        if (!select || !/^\d+$/.test(key)) return null;
        return select.querySelector('option[value="' + key + '"]');
      }

      onVariantEvent(event) {
        const d = event && event.detail;
        if (!d) return;
        const opt = this.optionFor(d.id);

        let options = null;
        if (Array.isArray(d.options)) {
          options = d.options.map((o) => (o && typeof o === 'object' ? o.value : o));
        } else if (typeof d.options === 'string' && d.options) {
          options = d.options.split('||');
        } else if (opt) {
          options = (opt.dataset.options || '').split('||');
        }

        let price = '';
        if (typeof d.price === 'string' && d.price) price = d.price;
        else if (opt && opt.dataset.price) price = opt.dataset.price;
        else if (typeof d.price === 'number') price = this.formatMoney(d.price);

        let available = true;
        if (typeof d.available === 'boolean') available = d.available;
        else if (opt) available = !opt.disabled;

        this.apply(options, price, available);
      }

      onSelectChange(event) {
        const el = event && event.target;
        if (!el || !el.matches || !el.matches('[data-vd-variant-select]')) return;
        this.applyOption(el.options[el.selectedIndex]);
      }

      applyOption(opt) {
        if (!opt) return;
        const options = (opt.dataset.options || '').split('||');
        this.apply(options, opt.dataset.price || '', !opt.disabled);
      }

      apply(options, price, available) {
        this.setFormat(options);
        if (price && this.priceTarget) this.priceTarget.textContent = price;
        if (this.button) {
          this.button.disabled = !available;
          // Libellé : celui du bouton principal s'il est déjà dans le même état
          // (il porte la langue servie par la section), sinon nos attributs.
          let label = '';
          if (this.target instanceof HTMLButtonElement && this.target.disabled === !available) {
            const labelEl = this.target.querySelector('.vd-pdp__add-label');
            label = (labelEl || this.target).textContent.trim();
          }
          if (!label) label = available ? this.dataset.labelAdd : this.dataset.labelUnavailable;
          if (label) this.button.textContent = label;
        }
      }

      /* Format choisi : les valeurs d'option jointes par « · ».
         Rien pour une variante unique (Default Title). */
      setFormat(options) {
        if (!this.formatTarget || !Array.isArray(options)) return;
        const values = options
          .map((v) => (v == null ? '' : String(v).trim()))
          .filter((v) => v && v !== 'Default Title');
        const text = values.join(' · ');
        this.formatTarget.textContent = text;
        this.formatTarget.hidden = !text;
        this.classList.toggle('vd-sticky-atc--no-format', !text);
      }

      /* Prix en centimes, si le nouveau sélecteur n'envoie pas de chaîne
         formatée. On reprend le format monétaire déposé dans la page par le
         sélecteur de format, sinon Intl. */
      formatMoney(cents) {
        const amount = (Number(cents) || 0) / 100;
        try {
          const data = document.querySelector('[data-vd-fmt-data]');
          const fmt = data && JSON.parse(data.textContent).devise;
          if (fmt && /\{\{\s*amount(_with_comma_separator)?\s*\}\}/.test(fmt)) {
            return fmt.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, amount.toFixed(2).replace('.', ','));
          }
          if (fmt && /\{\{\s*amount_no_decimals\s*\}\}/.test(fmt)) {
            return fmt.replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(amount)));
          }
        } catch (e) {
          /* format absent ou illisible : on passe par Intl */
        }
        const currency =
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr', {
            style: 'currency',
            currency: currency,
          }).format(amount);
        } catch (e) {
          return amount.toFixed(2).replace('.', ',') + ' ' + currency;
        }
      }

      /* ---------- Miroirs du bloc principal ---------- */

      syncButton() {
        this.button.disabled = this.target.disabled;
        const labelEl = this.target.querySelector('.vd-pdp__add-label');
        const label = (labelEl || this.target).textContent.trim();
        if (label) this.button.textContent = label;
      }

      syncPrice() {
        const current = this.priceSource.querySelector('.vd-pdp__price-current');
        const price = (current || this.priceSource).textContent.trim();
        if (price) this.priceTarget.textContent = price;
      }
    }
  );
}
